import React, { useState, useEffect } from 'react';
import { FaSearch, FaUser, FaPrayingHands, FaBriefcase, FaMapMarkerAlt, FaIdCard, FaChevronDown, FaChevronUp, FaUndo } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import CustomDropdown from '../common/UserCustomDropdown';
import api from "../../services/api"; // adjust path if needed


const Search = ({ onSearch }) => {
    const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'advanced', 'id'
    const [expandedSection, setExpandedSection] = useState('basic');
    const [masterData, setMasterData] = useState({
        religions: [],
        castes: {},
        subcastes: {},
        education: [],
        occupations: []
    });

    const [loadingMaster, setLoadingMaster] = useState(true);
    const [features, setFeatures] = useState({
        advancedSearch: false
    });



    const [filters, setFilters] = useState({
        // Basic
        ageFrom: 18,
        ageTo: 35,
        heightFrom: "4'5\"",
        heightTo: "7'0\"",
        maritalStatus: "",

        // Community
        religion: "",
        caste: "",
        subcaste: "",
        gothram: "",
        dosham: "",
        star: "",
        raasi: "",


        // Education & Career
        education: "",
        occupation: "",
        income: "",

        // Location
        country: "",
        state: "",
        currentCity: "",

        // Lifestyle
        diet: "",
        smoking: "",
        drinking: "",

        // Keywords
        keywords: ""
    });


    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    const handleCheckboxChange = (category, value) => {
        setFilters(prev => {
            const current = prev[category];
            if (current.includes(value)) {
                return { ...prev, [category]: current.filter(item => item !== value) };
            } else {
                return { ...prev, [category]: [...current, value] };
            }
        });
    };
    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const res = await api.get("/master-data-get");

                setMasterData(res.data.data);

                // 🔑 SAVE FEATURES
                if (res.data.features) {
                    setFeatures(res.data.features);
                }
            } catch (err) {
                console.error("Failed to load master data", err);
            } finally {
                setLoadingMaster(false);
            }
        };

        fetchMasterData();
    }, []);

    const religionOptions = masterData.religions.map(r => ({
        value: r,
        label: r.charAt(0).toUpperCase() + r.slice(1)
    }));

    const casteOptions = filters.religion
        ? (masterData.castes[filters.religion] || []).map(c => ({
            value: c,
            label: c
        }))
        : [];

    const subcasteOptions = filters.religion
        ? (masterData.subcastes[filters.religion] || []).map(sc => ({
            value: sc,
            label: sc
        }))
        : [];

    const educationOptions = masterData.education.map(ed => ({
        value: ed,
        label: ed
    }));

    const occupationOptions = masterData.occupations.map(occ => ({
        value: occ,
        label: occ
    }));

    const handleSearch = (e) => {
        e.preventDefault();
        console.log("Searching with filters:", filters);
        if (onSearch) {
            onSearch(filters);
        }
    };

    const resetFilters = () => {
        setFilters({
            ageFrom: 18,
            ageTo: 35,
            heightFrom: "4'5\"",
            heightTo: "7'0\"",
            maritalStatus: "",

            religion: "",
            caste: "",
            subcaste: "",
            gothram: "",
            dosham: "",
            star: "",
            rasi: "",


            education: "",
            occupation: "",
            income: "",

            country: "",
            state: "",
            currentCity: "",

            diet: "",
            smoking: "",
            drinking: "",

            keywords: ""
        });
    };


    // Dropdown options for various fields
    const dropdownOptions = {
        maritalStatus: [
            { value: 'never-married', label: 'Never Married' },
            { value: 'divorced', label: 'Divorced' },
            { value: 'widowed', label: 'Widowed' },
            { value: 'awaiting-divorce', label: 'Awaiting Divorce' }
        ],
        // physicalStatus: [
        //     { value: 'normal', label: 'Normal' },
        //     { value: 'physically-challenged', label: 'Physically Challenged' }
        // ],

        dosham: [
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
            { value: 'dont-know', label: "Don't Know" }
        ],

        annualIncome: [
            { value: 'any', label: 'Any' },
            { value: '0-3', label: '0 - 3 Lakhs' },
            { value: '3-6', label: '3 - 6 Lakhs' },
            { value: '6-10', label: '6 - 10 Lakhs' },
            { value: '10-15', label: '10 - 15 Lakhs' },
            { value: '15+', label: '15+ Lakhs' }
        ],
        country: [
            { value: 'india', label: 'India' },
            { value: 'usa', label: 'USA' },
            { value: 'uk', label: 'UK' },
            { value: 'canada', label: 'Canada' },
            { value: 'australia', label: 'Australia' },
            { value: 'singapore', label: 'Singapore' }
        ],
        state: [
            { value: 'tamil-nadu', label: 'Tamil Nadu' },
            { value: 'kerala', label: 'Kerala' },
            { value: 'karnataka', label: 'Karnataka' },
            { value: 'andhra', label: 'Andhra Pradesh' },
            { value: 'maharashtra', label: 'Maharashtra' }
        ],
        eatingHabits: [
            { value: 'vegetarian', label: 'Vegetarian' },
            { value: 'non-veg', label: 'Non-Veg' },
            { value: 'eggetarian', label: 'Eggetarian' }
        ],
        drinkingHabits: [
            { value: 'any', label: 'Any' },
            { value: 'never', label: 'Never' },
            { value: 'occasionally', label: 'Occasionally' },
            { value: 'regularly', label: 'Regularly' }
        ],
        smokingHabits: [
            { value: 'any', label: 'Any' },
            { value: 'never', label: 'Never' },
            { value: 'occasionally', label: 'Occasionally' },
            { value: 'regularly', label: 'Regularly' }
        ],

        motherTongue: [
            { value: 'tamil', label: 'Tamil' },
            { value: 'telugu', label: 'Telugu' },
            { value: 'malayalam', label: 'Malayalam' },
            { value: 'kannada', label: 'Kannada' },
            { value: 'hindi', label: 'Hindi' },
            { value: 'english', label: 'English' }
        ],
        employedIn: [
            { value: 'private', label: 'Private Sector' },
            { value: 'govt', label: 'Government' },
            { value: 'defense', label: 'Defense' },
            { value: 'business', label: 'Business' },
            { value: 'self-employed', label: 'Self Employed' }
        ],
        citizenship: [
            { value: 'indian', label: 'Indian' },
            { value: 'nri', label: 'NRI' },
            { value: 'oci', label: 'OCI' },
            { value: 'others', label: 'Others' }
        ]
    };
    const starOptions = [
        "Ashwini",
        "Bharani",
        "Krittika",
        "Rohini",
        "Mrigashira",
        "Ardra",
        "Punarvasu",
        "Pushya",
        "Ashlesha",
        "Magha",
        "Purva Phalguni",
        "Uttara Phalguni",
        "Hasta",
        "Chitra",
        "Swati",
        "Vishakha",
        "Anuradha",
        "Jyeshtha",
        "Mula",
        "Purva Ashadha",
        "Uttara Ashadha",
        "Shravana",
        "Dhanishta",
        "Shatabhisha",
        "Purva Bhadrapada",
        "Uttara Bhadrapada",
        "Revati",
    ].map(star => ({ value: star, label: star }));

    const rasiOptions = [
        "Mesha (Aries)",
        "Vrishabha (Taurus)",
        "Mithuna (Gemini)",
        "Kataka (Cancer)",
        "Simha (Leo)",
        "Kanya (Virgo)",
        "Tula (Libra)",
        "Vrischika (Scorpio)",
        "Dhanus (Sagittarius)",
        "Makara (Capricorn)",
        "Kumbha (Aquarius)",
        "Meena (Pisces)",
    ].map(rasi => ({ value: rasi, label: rasi }));

    // Generate age options
    const ageOptions = Array.from({ length: 43 }, (_, i) => ({
        value: 18 + i,
        label: `${18 + i}`
    }));

    // Height options
    const heightOptions = [
        { value: "4'5\"", label: "4'5\"" },
        { value: "5'0\"", label: "5'0\"" },
        { value: "5'5\"", label: "5'5\"" },
        { value: "6'0\"", label: "6'0\"" },
        { value: "6'5\"", label: "6'5\"" },
        { value: "7'0\"", label: "7'0\"" }
    ];

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 lg:mb-8 gap-4 lg:gap-8">
                <div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FaSearch className="text-rose-600" /> Find Your Perfect Match
                    </h2>
                    <p className="text-sm lg:text-base text-gray-500 mt-2">Use our advanced filters to find exactly who you're looking for.</p>
                </div>
                {/* Search Type Tabs */}
                <div className="w-full lg:w-auto flex flex-col sm:flex-row bg-white p-1 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {['basic',  'id','advanced'].map(tab => {
                        const isAdvancedLocked = tab === 'advanced' && !features.advancedSearch;

                        return (
                            <button
                                key={tab}
                                onClick={() => {
                                    if (!isAdvancedLocked) setActiveTab(tab);
                                }}
                                disabled={isAdvancedLocked}
                                className={`flex-1 sm:flex-none px-4 lg:px-6 py-3 lg:py-2 rounded-lg font-bold text-sm transition-all
                ${activeTab === tab
                                        ? 'bg-rose-600 text-white shadow-md'
                                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}
                ${isAdvancedLocked ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : ''}
            `}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)} Search
                                {isAdvancedLocked && <span className="ml-2">🔒</span>}
                            </button>
                        );
                    })}

                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Filters Column */}
                <div className="lg:col-span-12">
                    <form onSubmit={handleSearch} className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-visible">
                        {/* ID Search View */}
                        {activeTab === 'id' && (
                            <div className="p-12 flex flex-col items-center justify-center text-center space-y-6">
                                <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mb-4">
                                    <FaIdCard className="text-4xl text-rose-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800">Search by Profile ID</h3>
                                <p className="text-gray-500 max-w-md">Enter the unique Profile ID (e.g., SM123456) to view a specific member's profile directly.</p>
                                <div className="flex flex-col sm:flex-row w-full max-w-md gap-3">
                                    <input
                                        type="text"
                                        placeholder="Enter Profile ID..."
                                        className="flex-1 p-3 lg:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-base lg:text-lg"
                                    />
                                    <button className="w-full sm:w-auto px-8 py-3 lg:py-4 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-colors">
                                        View
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Basic & Advanced Search View */}
                        {activeTab !== 'id' && (
                            <div className="divide-y divide-gray-100">

                                {/* 🔒 ADVANCED SEARCH LOCK MESSAGE */}
                                {activeTab === 'advanced' && !features.advancedSearch && (
                                    <div className="p-12 text-center bg-gray-50">
                                        <h3 className="text-xl font-bold text-gray-700 mb-2">
                                            Advanced Search Locked 🔒
                                        </h3>
                                        <p className="text-gray-500">
                                            Upgrade your plan to unlock Advanced Search filters.
                                        </p>
                                    </div>
                                )}

                                {/* Section 1: Basic Details */}
                                <FilterSection
                                    title="Basic Details"
                                    icon={<FaUser />}
                                    isOpen={activeTab === 'basic' || expandedSection === 'basic'}
                                    onToggle={() => toggleSection('basic')}
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                        {/* Age Range */}
                                        <div className="space-y-2">
                                            <label className="font-semibold text-gray-700 text-sm lg:text-base">Age Range</label>
                                            <div className="flex items-center gap-2">
                                                <CustomDropdown
                                                    options={ageOptions}
                                                    value={filters.ageFrom}
                                                    onChange={(value) => setFilters({ ...filters, ageFrom: value })}
                                                    placeholder="From"
                                                    searchable={true}
                                                />
                                                <span className="text-gray-400 text-sm mx-2">to</span>
                                                <CustomDropdown
                                                    options={ageOptions}
                                                    value={filters.ageTo}
                                                    onChange={(value) => setFilters({ ...filters, ageTo: value })}
                                                    placeholder="To"
                                                    searchable={true}
                                                />
                                            </div>
                                        </div>

                                        {/* Height */}
                                        <div className="space-y-2">
                                            <label className="font-semibold text-gray-700 text-sm lg:text-base">Height</label>
                                            <div className="flex items-center gap-2">
                                                <CustomDropdown
                                                    options={heightOptions}
                                                    value={filters.heightFrom}
                                                    onChange={(value) => setFilters({ ...filters, heightFrom: value })}
                                                    placeholder="From"
                                                    searchable={true}
                                                />
                                                <span className="text-gray-400 text-sm mx-2">to</span>
                                                <CustomDropdown
                                                    options={heightOptions}
                                                    value={filters.heightTo}
                                                    onChange={(value) => setFilters({ ...filters, heightTo: value })}
                                                    placeholder="To"
                                                    searchable={true}
                                                />
                                            </div>
                                        </div>

                                        {/* Marital Status */}
                                        <div className="space-y-2">
                                            <label className="font-semibold text-gray-700 text-sm lg:text-base">Marital Status</label>
                                            <CustomDropdown
                                                options={dropdownOptions.maritalStatus}
                                                value={filters.maritalStatus}
                                                onChange={(value) => setFilters({ ...filters, maritalStatus: value })}
                                                placeholder="Select Marital Status"
                                                searchable={true}
                                            />
                                        </div>

                                        {/* Physical Status */}
                                        {/* <div className="space-y-2">
                                            <label className="font-semibold text-gray-700 text-sm lg:text-base">Physical Status</label>
                                            <CustomDropdown
                                                options={dropdownOptions.physicalStatus}
                                                value={filters.physicalStatus}
                                                onChange={(value) => setFilters({ ...filters, physicalStatus: value })}
                                                placeholder="Select Physical Status"
                                                searchable={true}
                                            />
                                        </div> */}
                                    </div>
                                </FilterSection>

                                {/* Advanced Sections */}
                                {activeTab === 'advanced' && (
                                    <>
                                        {/* Religious Information */}
                                        <FilterSection
                                            title="Religious Information"
                                            icon={<FaPrayingHands />}
                                            isOpen={expandedSection === 'religious'}
                                            onToggle={() => toggleSection('religious')}
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Religion</label>
                                                    <CustomDropdown
                                                        options={religionOptions}
                                                        value={filters.religion}
                                                        onChange={(value) => setFilters({ ...filters, religion: value, caste: "", subcaste: "" })}
                                                        placeholder="Select Religion"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Caste</label>
                                                    <CustomDropdown
                                                        options={casteOptions}
                                                        value={filters.caste}
                                                        onChange={(value) =>
                                                            setFilters({ ...filters, caste: value, subcaste: "" })
                                                        }
                                                        placeholder={
                                                            filters.religion ? "Select Caste" : "Select Religion first"
                                                        }
                                                        disabled={!filters.religion}
                                                        searchable
                                                    />

                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Sub Caste</label>
                                                    <CustomDropdown
                                                        options={subcasteOptions}
                                                        value={filters.subcaste}
                                                        onChange={(value) =>
                                                            setFilters({ ...filters, subcaste: value })
                                                        }
                                                        placeholder={
                                                            filters.religion ? "Select Subcaste" : "Select Religion first"
                                                        }
                                                        disabled={!filters.religion}
                                                        searchable
                                                    />

                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Dosham</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.dosham}
                                                        value={filters.dosham}
                                                        onChange={(value) => setFilters({ ...filters, dosham: value })}
                                                        placeholder="Select Dosham"
                                                        searchable={true}
                                                    />
                                                </div>
                                                {/* Star */}
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Star (Nakshatra)</label>
                                                    <CustomDropdown
                                                        options={starOptions}
                                                        value={filters.star}
                                                        onChange={(value) => setFilters({ ...filters, star: value })}
                                                        placeholder="Select Star"
                                                        searchable
                                                    />
                                                </div>

                                                {/* Rasi */}
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Rasi</label>
                                                    <CustomDropdown
                                                        options={rasiOptions}
                                                        value={filters.rasi}
                                                        onChange={(value) => setFilters({ ...filters, rasi: value })}
                                                        placeholder="Select Rasi"
                                                        searchable
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Gothram</label>
                                                    <input
                                                        type="text"
                                                        className="form-input w-full rounded-lg border-gray-300 py-2 px-4"
                                                        placeholder="Enter Gothram"
                                                        value={filters.gothram}
                                                        onChange={(e) => setFilters({ ...filters, gothram: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </FilterSection>

                                        {/* Professional Details */}
                                        <FilterSection
                                            title="Professional Details"
                                            icon={<FaBriefcase />}
                                            isOpen={expandedSection === 'professional'}
                                            onToggle={() => toggleSection('professional')}
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Education</label>
                                                    <CustomDropdown
                                                        options={educationOptions}
                                                        value={filters.education}
                                                        onChange={(value) => setFilters({ ...filters, education: value })}
                                                        placeholder={loadingMaster ? "Loading..." : "Select Education"}
                                                        disabled={loadingMaster}
                                                        searchable
                                                    />

                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Occupation</label>
                                                    <CustomDropdown
                                                        options={occupationOptions}
                                                        value={filters.occupation}
                                                        onChange={(value) => setFilters({ ...filters, occupation: value })}
                                                        placeholder={loadingMaster ? "Loading..." : "Select Occupation"}
                                                        disabled={loadingMaster}
                                                        searchable
                                                    />

                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Employed In</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.employedIn}
                                                        value={filters.employedIn}
                                                        onChange={(value) => setFilters({ ...filters, employedIn: value })}
                                                        placeholder="Select Employment Type"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Annual Income</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.annualIncome}
                                                        value={filters.annualIncome}
                                                        onChange={(value) => setFilters({ ...filters, annualIncome: value })}
                                                        placeholder="Select Income Range"
                                                        searchable={true}
                                                    />
                                                </div>
                                            </div>
                                        </FilterSection>

                                        {/* Location & Lifestyle */}
                                        <FilterSection
                                            title="Location & Lifestyle"
                                            icon={<FaMapMarkerAlt />}
                                            isOpen={expandedSection === 'location'}
                                            onToggle={() => toggleSection('location')}
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Country</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.country}
                                                        value={filters.country}
                                                        onChange={(value) => setFilters({ ...filters, country: value })}
                                                        placeholder="Select Country"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">State</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.state}
                                                        value={filters.state}
                                                        onChange={(value) => setFilters({ ...filters, state: value })}
                                                        placeholder="Select State"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">City</label>
                                                    <input
                                                        type="text"
                                                        className="form-input w-full rounded-lg border-gray-300 py-2 px-4"
                                                        placeholder="Enter City"
                                                        value={filters.city}
                                                        onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Citizenship</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.citizenship}
                                                        value={filters.citizenship}
                                                        onChange={(value) => setFilters({ ...filters, citizenship: value })}
                                                        placeholder="Select Citizenship"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Eating Habits</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.eatingHabits}
                                                        value={filters.eatingHabits}
                                                        onChange={(value) => setFilters({ ...filters, eatingHabits: value })}
                                                        placeholder="Select Eating Habits"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Drinking Habits</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.drinkingHabits}
                                                        value={filters.drinkingHabits}
                                                        onChange={(value) => setFilters({ ...filters, drinkingHabits: value })}
                                                        placeholder="Select Drinking Habits"
                                                        searchable={true}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="font-semibold text-gray-700">Smoking Habits</label>
                                                    <CustomDropdown
                                                        options={dropdownOptions.smokingHabits}
                                                        value={filters.smokingHabits}
                                                        onChange={(value) => setFilters({ ...filters, smokingHabits: value })}
                                                        placeholder="Select Smoking Habits"
                                                        searchable={true}
                                                    />
                                                </div>
                                            </div>
                                        </FilterSection>
                                    </>
                                )}

                                {/* Keywords Section */}
                                <div className="p-6 border-b border-gray-100">
                                    <div className="space-y-2">
                                        <label className="font-semibold text-gray-700 text-sm lg:text-base">Keywords</label>
                                        <input
                                            type="text"
                                            placeholder="Search by keywords (e.g., doctor, engineer, vegetarian...)"
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none"
                                            value={filters.keywords}
                                            onChange={(e) => setFilters({ ...filters, keywords: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="p-8 bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
                                    <button
                                        type="button"
                                        onClick={resetFilters}
                                        className="flex items-center gap-2 text-gray-500 font-bold hover:text-gray-700 transition-colors"
                                    >
                                        <FaUndo /> Reset Filters
                                    </button>
                                    <button
                                        type="submit"
                                        className="w-full md:w-auto px-12 py-4 bg-gradient-to-r from-rose-600 to-pink-600 text-white font-bold rounded-xl shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 hover:scale-105 transition-all text-lg"
                                    >
                                        Search Matches
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

const FilterSection = ({ title, icon, children, isOpen, onToggle }) => (
    <div className="border-b border-gray-100 last:border-0">
        <button
            type="button"
            onClick={onToggle}
            className={`w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
        >
            <div className="flex items-center gap-3">
                <span className={`text-xl ${isOpen ? 'text-rose-600' : 'text-gray-400'}`}>{icon}</span>
                <span className={`font-bold text-lg ${isOpen ? 'text-gray-800' : 'text-gray-600'}`}>{title}</span>
            </div>
            {isOpen ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </button>
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-visible"
                >
                    <div className="p-6 pt-0">
                        {children}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

export default Search;